import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { getImageUrl } from '../../lib/utils-img';
import API from '../../lib/api';
import { toast } from 'sonner';

export default function AdminListings() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (authLoading) return; if (!isAdmin) { navigate('/login'); return; } load(); loadLocations(); }, [isAdmin, authLoading, navigate]);
  const load = (locId) => { API.get('/admin/listings', { params: locId ? { location_id: locId } : {} }).then(r => { setListings(r.data.listings); setLoading(false); }).catch(() => setLoading(false)); };
  const loadLocations = () => { API.get('/admin/locations').then(r => setLocations(r.data.locations)).catch(() => {}); };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await API.delete(`/admin/listings/${id}`); toast.success('Listing deleted'); load(filter || undefined); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const toggleVisibility = async (listing) => {
    try { await API.put(`/admin/listings/${listing.id}`, { is_visible: !listing.is_visible }); toast.success(`Listing ${listing.is_visible ? 'hidden' : 'visible'}`); load(filter || undefined); }
    catch { toast.error('Update failed'); }
  };

  const handleFilter = (locId) => { setFilter(locId); setLoading(true); load(locId || undefined); };

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-listings">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p><h1 className="font-heading text-2xl text-white font-bold mt-1">Listings</h1></div>
          <Link to="/admin/listings/new"><Button className="bg-gold hover:bg-[#9A7B1F] text-white text-xs uppercase tracking-widest rounded-lg gap-1.5" data-testid="new-listing-btn"><Plus className="w-3.5 h-3.5" /> New Listing</Button></Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <AdminNav active="/admin/listings" />
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <button onClick={() => handleFilter('')} className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors whitespace-nowrap ${!filter ? 'bg-gold text-white' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'}`} data-testid="filter-all">All</button>
          {locations.map(loc => (
            <button key={loc.id} onClick={() => handleFilter(loc.id)} className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors whitespace-nowrap ${filter === loc.id ? 'bg-gold text-white' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'}`} data-testid={`filter-${loc.slug}`}>{loc.name}</button>
          ))}
        </div>
        {loading ? <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />)}</div> : listings.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/35 mb-4">No listings yet.</p>
            <Link to="/admin/listings/new"><Button className="bg-gold hover:bg-[#9A7B1F] text-white rounded-lg text-xs uppercase tracking-widest gap-1.5"><Plus className="w-3.5 h-3.5" /> Create Listing</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map((l, i) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all" data-testid={`listing-row-${l.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-10 sm:w-16 sm:h-12 rounded-lg bg-white/[0.06] overflow-hidden shrink-0">
                    {l.cover_image && <img src={getImageUrl(l.cover_image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-white truncate">{l.title}</h3>
                      {l.badges?.map(b => <Badge key={b} className="bg-gold/15 text-gold border-0 text-[10px] px-1.5 py-0">{b}</Badge>)}
                      {!l.is_visible && <span className="text-[10px] bg-white/[0.05] text-white/30 px-1.5 py-0.5 rounded font-medium">Hidden</span>}
                    </div>
                    <p className="text-xs text-white/35 truncate">{l.location_name} &middot; {l.guest_capacity} guests &middot; {l.bedrooms} bed</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 sm:mt-0 border-t border-white/[0.04] sm:border-0 pt-3 sm:pt-0">
                  {l.hostaway_booking_url && <a href={l.hostaway_booking_url} target="_blank" rel="noopener noreferrer" className="p-2 text-white/20 hover:text-blue-400 transition-colors" title="Booking URL"><ExternalLink className="w-4 h-4" /></a>}
                  <button onClick={() => toggleVisibility(l)} className={`p-2 rounded-lg transition-colors ${l.is_visible ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-white/20 hover:bg-white/[0.05]'}`}>{l.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <Link to={`/admin/listings/${l.id}/edit`} className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/[0.05] transition-colors" data-testid={`edit-listing-${l.id}`}><Edit className="w-4 h-4" /></Link>
                  <button onClick={() => handleDelete(l.id, l.title)} className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
